/**
 * Knocking on a server.
 *
 * Opens a socket, says hello and waits to be welcomed. That is the whole of
 * it - it does not play the game, it answers "is there a world at this
 * address, and does it speak our version".
 *
 * Worth having on its own, before the client can play, because every failure
 * a person will meet setting this up happens here: a server that is not
 * running, a firewall, a typo, a port, an old build on one side. "No answer"
 * and "wrong version" are different problems with different fixes, and a
 * screen that could not tell them apart would send people to the wrong one.
 *
 * platform/, not core/: it is a socket, and core has never heard of one.
 */

import {
  PROTOCOL_VERSION, encode, decode, type WorldOffer,
} from "../core/net/protocol.js";
import { reason } from "./hosting.js";

/**
 * What a knock came back with. Each refusal the server can give is its
 * own verdict, because each wants a different sentence: the first version
 * called everything but a wrong word a version mismatch, and a world that
 * was not there read as "Сервер говорить іншою версією протоколу".
 */
export type ProbeVerdict =
  | "ok" | "mismatch" | "wrong-word" | "unreachable"
  | "gone" | "full" | "late" | "name-taken";

/**
 * What was there, and what it is offering.
 *
 * The worlds come back from the same knock that says the server is alive,
 * because they are the same question: "is there a game at this address" and
 * "which games" are one round trip, and asking twice would mean the picker
 * could show a list from a server that had gone away in between.
 */
export interface ProbeResult {
  readonly verdict: ProbeVerdict;
  readonly worlds: readonly WorldOffer[];
}

/** How long to wait before deciding nobody is home. */
const PATIENCE = 4000;

/**
 * Knock, and optionally try one world's door.
 *
 * With no `world` this asks what is here and needs no word - the word
 * belongs to the world now, so there is nothing to check until one is named.
 * With one, it tries that world's word and comes back "ok" or "wrong-word"
 * **without going in**: the answer has to arrive before the page reloads to
 * join, or a mistyped word would be a кремль grown for somebody who is not
 * coming and a blank failure a second later.
 */
/**
 * Ask the account desk one thing, and give back what it said.
 *
 * Its own socket and its own function rather than a branch inside the probe:
 * registering, forgetting and resetting are not knocking on a world, they
 * happen before there is any question of which world, and none of them needs
 * the word.
 *
 * @returns the key of what it said, which the interface translates - the
 * server never decides what language anybody reads.
 */
export function askAccount(
  url: string, message: object,
): Promise<{ ok: boolean; why: string; token?: string }> {
  return new Promise((resolve) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      resolve({ ok: false, why: "net-unreachable" });
      return;
    }
    let settled = false;
    const finish = (answer: { ok: boolean; why: string; token?: string }): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { socket.close(); } catch { /* already gone */ }
      resolve(answer);
    };
    const timer = setTimeout(() => finish(
      { ok: false, why: "net-unreachable" }), PATIENCE);

    socket.addEventListener("open", () => socket.send(encode(message as never)));
    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      const said = decode(event.data);
      if (said === null || said.t !== "account") return;
      finish({
        ok: said.ok,
        why: said.why ?? "",
        ...(said.token !== undefined ? { token: said.token } : {}),
      });
    });
    socket.addEventListener("error", () => finish(
      { ok: false, why: "net-unreachable" }));
    socket.addEventListener("close", () => finish(
      { ok: false, why: "net-unreachable" }));
  });
}

/**
 * Ask whether our server would take a world held here, before reloading
 * into one. The same `host` the page sends after the reload, marked as a
 * question - see the protocol - so a refusal is known while the form is
 * still on the screen. Answers with the ui key for what went wrong.
 */
export function askHosting(
  url: string, map: string, name: string, token: string, password: string,
): Promise<{ ok: boolean; why: string }> {
  return new Promise((resolve) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      resolve({ ok: false, why: "net-unreachable" });
      return;
    }
    let settled = false;
    const finish = (answer: { ok: boolean; why: string }): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { socket.close(); } catch { /* already gone */ }
      resolve(answer);
    };
    const timer = setTimeout(() => finish(
      { ok: false, why: "net-unreachable" }), PATIENCE);
    socket.addEventListener("open", () => socket.send(encode({
      t: "host", map, name, token, password, locked: false, dry: true,
    })));
    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      const said = decode(event.data);
      if (said === null || said.t !== "hosting") return;
      finish(said.ok ? { ok: true, why: "" } : { ok: false, why: reason(said.why ?? "") });
    });
    socket.addEventListener("error", () => finish(
      { ok: false, why: "net-unreachable" }));
    socket.addEventListener("close", () => finish(
      { ok: false, why: "net-unreachable" }));
  });
}

export function probeServer(
  url: string, word = "", world?: string,
): Promise<ProbeResult> {
  return new Promise((resolve) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      // A malformed url throws here rather than firing onerror.
      resolve({ verdict: "unreachable", worlds: [] });
      return;
    }

    let settled = false;
    const finish = (
      verdict: ProbeVerdict, worlds: readonly WorldOffer[] = [],
    ): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { socket.close(); } catch { /* already gone */ }
      resolve({ verdict, worlds });
    };
    const timer = setTimeout(() => finish("unreachable"), PATIENCE);

    socket.addEventListener("open", () => {
      /*
       * `look`, so that naming a world does not enter it.
       *
       * A hello that names a world and means it is what grows one, and a
       * knock has not asked for a кремль to be built.
       */
      socket.send(encode({
        t: "hello", v: PROTOCOL_VERSION, name: "?", skinId: "kozak", word,
        ...(world !== undefined ? { world, look: true } : {}),
      }));
    });
    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      const message = decode(event.data);
      if (message === null) return;
      if (message.t === "worlds") finish("ok", message.list);
      // The answer to a `look`: that word does open that world.
      else if (message.t === "open") finish("ok");
      /*
       * A welcome also means yes.
       *
       * Nothing sends one to a knock any more, but a server that did would
       * be answering the question this probe asks - and reading it as
       * silence would report "nobody home" about a machine that had just
       * said hello.
       */
      else if (message.t === "welcome") finish("ok");
      // Refused by name rather than by going quiet, which is the difference
      // this whole probe is for: a wrong word, an old build and a machine
      // that is not there need three different things doing about them.
      else if (message.t === "no") {
        const code = String(message.code ?? "");
        finish(code === "word" ? "wrong-word"
          : code === "world" ? "gone"
          : code === "full" ? "full"
          : code === "late" ? "late"
          : code === "name" ? "name-taken"
          : "mismatch");
      }
    });
    socket.addEventListener("error", () => finish("unreachable"));
    socket.addEventListener("close", () => finish("unreachable"));
  });
}
